import { SuperAdminShell } from './SuperAdminDashboard';
import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

export default function SuperAdminTimetablePage() {
  const { addToast } = useToast();
  const [cohorts, setCohorts]     = useState([]);
  const [selected, setSelected]   = useState('');
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [form, setForm]           = useState({ week_number:1, day:'Monday', date:'', topic:'', description:'', meet_link:'' });
  const [saving, setSaving]       = useState(false);
  const [editId, setEditId]       = useState(null);

  useEffect(() => {
    API.get('/cohorts').then(({ data }) => setCohorts(data.cohorts || [])).catch(() => {});
  }, []);

  const loadTimetable = (cohortId) => {
    setLoading(true);
    API.get(`/timetable/cohort/${cohortId}`)
      .then(({ data }) => setTimetable(data.timetable || []))
      .catch(() => addToast('Failed to load timetable', 'error'))
      .finally(() => setLoading(false));
  };

  const handleSelect = (id) => { setSelected(id); if (id) loadTimetable(id); };

  const handleSave = async () => {
    if (!form.topic || !form.date || !selected) { addToast('Topic, date, and cohort required', 'error'); return; }
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/timetable/${editId}`, form);
        addToast('Entry updated!', 'success');
      } else {
        await API.post('/timetable', { ...form, cohort_id: parseInt(selected) });
        addToast('Entry added!', 'success');
      }
      setForm({ week_number:1, day:'Monday', date:'', topic:'', description:'', meet_link:'' });
      setEditId(null);
      loadTimetable(selected);
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/timetable/${id}`);
      addToast('Deleted', 'success');
      setTimetable(p => p.filter(t => t.id !== id));
    } catch { addToast('Delete failed', 'error'); }
  };

  const handleEdit = (entry) => {
    setForm({ week_number:entry.week_number, day:entry.day, date:entry.date?.split('T')[0]||'', topic:entry.topic, description:entry.description||'', meet_link:entry.meet_link||'' });
    setEditId(entry.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Bulk auto-fill 10 weeks from cohort start date
  const handleBulkCreate = async () => {
    const cohort = cohorts.find(c => String(c.id) === String(selected));
    if (!cohort) return;
    const days = Array.isArray(cohort.days) ? cohort.days : JSON.parse(cohort.days || '[]');
    const dayOrder = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const entries = [];
    const start = new Date(cohort.start_date);

    for (let week = 1; week <= 10; week++) {
      for (const dayName of days) {
        const dayNum = dayOrder.indexOf(dayName);
        if (dayNum < 0) continue;
        const classDate = new Date(start);
        const startDay = start.getDay();
        let diff = dayNum - startDay;
        if (diff < 0) diff += 7;
        classDate.setDate(start.getDate() + diff + (week - 1) * 7);
        entries.push({
          week_number: week,
          day: dayName,
          date: classDate.toISOString().split('T')[0],
          topic: `Week ${week} — Class`,
          meet_link: cohort.meet_link || '',
        });
      }
    }

    setSaving(true);
    try {
      await API.post('/timetable/bulk', { cohort_id: parseInt(selected), entries });
      addToast(`Created ${entries.length} timetable slots!`, 'success');
      loadTimetable(selected);
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  // Group by week
  const byWeek = timetable.reduce((acc, t) => {
    const w = t.week_number;
    if (!acc[w]) acc[w] = [];
    acc[w].push(t);
    return acc;
  }, {});

  return (
    <SuperAdminShell title="Timetable" subtitle="Manage weekly class schedules for each cohort">
    <div className="space-y-6">

      {/* Cohort selector */}
      <Card className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <select value={selected} onChange={e => handleSelect(e.target.value)}
            className="flex-1 min-w-48 px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
            <option value="">— Select a cohort —</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} ({c.level})</option>)}
          </select>
          {selected && (
            <Button variant="secondary" size="sm" loading={saving} onClick={handleBulkCreate}>
              ⚡ Auto-fill 10 Weeks
            </Button>
          )}
        </div>
      </Card>

      {selected && (
        <>
          {/* Add/Edit form */}
          <Card className="p-5" accent="red">
            <h3 className="text-sm font-bold text-de-black mb-4">{editId ? 'Edit Entry' : 'Add Timetable Entry'}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-de-black mb-1">Week</label>
                <input type="number" min="1" max="10" value={form.week_number}
                  onChange={e => setForm(p => ({...p, week_number:parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/>
              </div>
              <div>
                <label className="block text-xs font-bold text-de-black mb-1">Day</label>
                <select value={form.day} onChange={e => setForm(p => ({...p, day:e.target.value}))}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                  {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-de-black mb-1">Date</label>
                <input type="date" value={form.date}
                  onChange={e => setForm(p => ({...p, date:e.target.value}))}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-de-black mb-1">Topic *</label>
                <input type="text" value={form.topic} onChange={e => setForm(p => ({...p, topic:e.target.value}))}
                  placeholder="e.g. German Articles: der, die, das"
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/>
              </div>
              <div>
                <label className="block text-xs font-bold text-de-black mb-1">Meet Link</label>
                <input type="url" value={form.meet_link} onChange={e => setForm(p => ({...p, meet_link:e.target.value}))}
                  placeholder="https://meet.google.com/..."
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-3">
              {editId && <Button variant="secondary" onClick={() => { setEditId(null); setForm({ week_number:1, day:'Monday', date:'', topic:'', description:'', meet_link:'' }); }}>Cancel</Button>}
              <Button variant="primary" loading={saving} onClick={handleSave}>{editId ? 'Update' : '+ Add Entry'}</Button>
            </div>
          </Card>

          {/* Timetable grid */}
          {loading ? (
            <div className="flex justify-center py-10"><Spinner className="text-brand-500"/></div>
          ) : Object.keys(byWeek).length === 0 ? (
            <Card className="p-8 text-center text-gray-400 text-sm">No timetable entries yet. Add entries above or use Auto-fill.</Card>
          ) : (
            Object.keys(byWeek).sort((a,b) => +a - +b).map(week => (
              <Card key={week} className="overflow-hidden">
                <div className="px-5 py-3 bg-de-black text-white">
                  <p className="text-sm font-bold">Week {week}</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {byWeek[week].map(entry => (
                    <div key={entry.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="text-center w-16">
                          <p className="text-xs font-bold text-brand-500">{entry.day.slice(0,3)}</p>
                          <p className="text-xs text-gray-500">{entry.date?.split('T')[0]}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-de-black">{entry.topic}</p>
                          {entry.meet_link && (
                            <a href={entry.meet_link} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-brand-500 hover:underline">🔗 Meet Link</a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => handleEdit(entry)}>Edit</Button>
                        <Button size="sm" variant="danger"    onClick={() => handleDelete(entry.id)}>Del</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))
          )}
        </>
      )}
    </div>
    </SuperAdminShell>
  );
}
